import type { MessageReplyPagesAndParams } from '@aurora/shared-client/routes/endUserRoutes';
import { SortDirection } from '@aurora/shared-generated/types/graphql-schema-types';
import type { ContextMessageFragment } from '@aurora/shared-generated/types/graphql-types';
import UrlHelper from '@aurora/shared-utils/helpers/urls/UrlHelper/UrlHelper';
import type { EndUserRouter } from '@aurora/shared-client/routes/useEndUserRoutes';
import UrlBuilder from '@aurora/shared-utils/helpers/urls/UrlHelper/UrlBuilder';
import { stripHtmlFromString } from '@aurora/shared-utils/helpers/objects/StringHelper';
import ConversationStyleBehaviorHelper from '../boards/ConversationStyleBehaviorHelper';
import type {
  MessageViewsQueryVariables,
  QaPageMessageDataFragment
} from '../../types/graphql-types';

interface Author {
  /**
   * Name of the author
   */
  name: string;
}

export interface Solution {
  /**
   * Body of the message
   */
  text: string;
  /**
   * Post time of the reply
   */
  dateCreated: string;
  /**
   * Kudos count of the root message
   */
  upvoteCount: number;
  /**
   * Permalink url of the reply
   */
  url: string;
  /**
   * Author of the root message
   */
  author: Author;
}

/**
 * @param contextMessage Context Message
 * @param hasSolution Boolean value for solution field
 * @param first the page size
 * @param postTimeSort type of sort on post time field
 *
 * @returns Query variables for Message View call
 */
export const queryVariables = (
  contextMessage: ContextMessageFragment,
  hasSolution: boolean,
  first = 1
): MessageViewsQueryVariables => {
  if (!contextMessage) {
    return null;
  }
  return {
    constraints: {
      topicId: {
        eq: contextMessage.id
      },
      solution: {
        eq: hasSolution
      },
      boardId: {
        eq: contextMessage.board.id
      },
      ...(!hasSolution && {
        depth: {
          neq: 0
        }
      })
    },
    sorts: {
      kudosSumWeight: {
        direction: SortDirection.Desc,
        order: 0
      },
      postTime: {
        direction: SortDirection.Asc,
        order: 1
      }
    },
    first,
    useBody: true,
    useKudosCount: true,
    useUserHoverCard: false
  };
};

/**
 * @param node Message
 * @returns Transformed Solution
 */
export function transformSolution(
  node: QaPageMessageDataFragment,
  router: EndUserRouter,
  baseUrl: string,
  getCaseSensitivePath: (value: string) => string
): Solution {
  const {
    uid,
    conversation: { topic },
    board,
    body,
    postTime,
    kudosSumWeight,
    author: { login }
  } = node;

  const { messageReplyPage } = ConversationStyleBehaviorHelper.getInstance(board);
  const relativeUrlForRoute = router.getRelativeUrlForRoute<MessageReplyPagesAndParams>(
    messageReplyPage,
    {
      boardId: getCaseSensitivePath(board.displayId),
      messageSubject: UrlHelper.determineSlugForMessagePath(topic),
      messageId: topic.uid.toString(),
      replyId: uid.toString()
    }
  );

  const url = UrlBuilder.fromUrl(baseUrl).addPath(relativeUrlForRoute).build();

  return {
    text: stripHtmlFromString(body),
    dateCreated: postTime,
    upvoteCount: kudosSumWeight,
    url,
    author: {
      name: getCaseSensitivePath(login)
    }
  };
}
